import React, { useEffect, useRef, useState } from 'react';
import { autodiscoveryAPI, configAPI, hostsAPI } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import { Globe } from 'lucide-react';

const EMPTY_FORM = {
  name: '', domains: '', additional_fqdns: '', whitelist_ips: '',
  whitelist_subdomains_regex: '', exclude_hosts: '', blacklist_ips: '',
  provider_config: '', enabled: false, auto_enrich: false,
  katana_rate_limit: 5, katana_workers: 1, katana_extra_flags: '', httpx_ports: '',
  // Waymore (исторические URL из веб-архивов)
  enable_waymore: false,
  waymore_threads: 2,
  waymore_mode: 'U',
  waymore_limit: '',
  waymore_filter_codes: '301,302,304,404',
  waymore_filter_types: 'text/css,image/jpeg,image/png,image/gif,image/svg+xml,font/woff,font/woff2',
  waymore_from_date: '',
  waymore_to_date: '',
  waymore_no_subs: false,
  waymore_wayback_rl_retry: 180,
  waymore_urlscan_rl_retry: 60,
  waymore_soft_timeout: 1800,
  waymore_hard_timeout: 3600,
};

const AutoDiscovery = () => {
  const { showSuccess, showError } = useNotification();
  const [defs, setDefs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [runs, setRuns] = useState([]);
  const [selectedDef, setSelectedDef] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [scheduleMode, setScheduleMode] = useState('interval'); // 'interval' | 'cron'
  const [intervalPreset, setIntervalPreset] = useState('15m');
  const [intervalCustom, setIntervalCustom] = useState('');
  const [cronExpr, setCronExpr] = useState('');
  const [viewingRunData, setViewingRunData] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  
  // Classification state
  const [enableClassification, setEnableClassification] = useState(false);
  const [classificationPrompt, setClassificationPrompt] = useState('');
  const [classificationModel, setClassificationModel] = useState('openai/gpt-oss-20b:free');
  const [classificationApiKey, setClassificationApiKey] = useState('');
  const [classificationParallelHosts, setClassificationParallelHosts] = useState(5);
  const [classificationApiKeyConfigured, setClassificationApiKeyConfigured] = useState(false);
  const [clearClassificationApiKey, setClearClassificationApiKey] = useState(false);
  const [defaultPrompt, setDefaultPrompt] = useState('');
  const [classificationResults, setClassificationResults] = useState([]);
  const [progressData, setProgressData] = useState(null);
  const [showProgress, setShowProgress] = useState(false);
  // Keep the selected def id reachable from the 15s polling interval without
  // re-creating the interval (fixes a stale closure that reset the run filter
  // to "all runs" every poll once a definition was selected).
  const selectedDefRef = useRef(null);
  selectedDefRef.current = selectedDef;

  const loadDefs = async () => {
    try {
      setLoading(true);
      const res = await autodiscoveryAPI.listDefs();
      setDefs(res.data.definitions || []);
    } catch (e) {
      showError(e.response?.data?.message || 'Не удалось загрузить определения');
    } finally {
      setLoading(false);
    }
  };

  const loadRuns = async (defId = null) => {
    try {
      const res = await autodiscoveryAPI.listRuns({ defId, page: 1, limit: 20 });
      setRuns(res.data.runs || []);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    loadDefs();
    loadRuns(null);
    loadDefaultPrompt();
    const interval = setInterval(() => {
      loadDefs();
      loadRuns(selectedDefRef.current?._id || null);
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  // Auto-poll progress for running scans
  useEffect(() => {
    const runningRun = runs.find(r => r.status === 'running' || r.status === 'pending');
    if (!runningRun) {
      if (progressData) setProgressData(null);
      return;
    }
    const pollProgress = async () => {
      try {
        const res = await autodiscoveryAPI.getRunProgress(runningRun._id);
        setProgressData(res.data);
      } catch (e) {
        // ignore
      }
    };
    pollProgress();
    const interval = setInterval(pollProgress, 3000);
    return () => clearInterval(interval);
  }, [runs]);

  const loadDefaultPrompt = async () => {
    try {
      const res = await autodiscoveryAPI.getDefaultPrompt();
      setDefaultPrompt(res.data.prompt || '');
      setClassificationPrompt(res.data.prompt || '');
    } catch (e) {
      console.error('Не удалось загрузить prompt по умолчанию:', e);
    }
  };

  const submitForm = async (e) => {
    e.preventDefault();
    try {
      let intervalExpr = intervalCustom.trim() || intervalPreset;
      if (scheduleMode === 'interval') {
        if (!/^\d+(m|h)$/.test(intervalExpr)) {
          showError('Интервал должен быть в формате 15m или 1h');
          return;
        }
      } else {
        if (!cronExpr.trim()) {
          showError('CRON-выражение не может быть пустым');
          return;
        }
      }
      const payload = {
        name: form.name.trim(),
        domains: form.domains.split(/\s|,|;/).map(s => s.trim().replace(/:(80|443)$/, '')).filter(Boolean),
        additional_fqdns: form.additional_fqdns.split(/\s|,|;/).map(s => s.trim().replace(/:(80|443)$/, '')).filter(Boolean),
        whitelist_ips: form.whitelist_ips.split(/\s|,|;/).map(s => s.trim()).filter(Boolean),
        whitelist_subdomains_regex: form.whitelist_subdomains_regex.trim(),
        exclude_hosts: form.exclude_hosts.split(/\s|,|;/).map(s => s.trim().replace(/:(80|443)$/, '')).filter(Boolean),
        blacklist_ips: form.blacklist_ips.split(/\s|,|;/).map(s => s.trim()).filter(Boolean),
        provider_config: form.provider_config.trim(),
        katana_rate_limit: Number.isFinite(parseInt(form.katana_rate_limit)) ? parseInt(form.katana_rate_limit) : 5,
        katana_workers: Number.isFinite(parseInt(form.katana_workers)) ? Math.max(1, Math.min(parseInt(form.katana_workers), 20)) : 1,
        katana_extra_flags: (form.katana_extra_flags || '').trim(),
        httpx_ports: form.httpx_ports.trim(),
        // Waymore section
        enable_waymore: !!form.enable_waymore,
        waymore_threads: Number.isFinite(parseInt(form.waymore_threads)) ? Math.max(1, Math.min(parseInt(form.waymore_threads), 5)) : 2,
        waymore_mode: form.waymore_mode || 'U',
        waymore_limit: form.waymore_limit && parseInt(form.waymore_limit) > 0 ? parseInt(form.waymore_limit) : null,
        waymore_filter_codes: (form.waymore_filter_codes || '').trim() || null,
        waymore_filter_types: (form.waymore_filter_types || '').trim() || null,
        waymore_from_date: (form.waymore_from_date || '').trim() || null,
        waymore_to_date: (form.waymore_to_date || '').trim() || null,
        waymore_no_subs: !!form.waymore_no_subs,
        waymore_wayback_rl_retry: Number.isFinite(parseInt(form.waymore_wayback_rl_retry)) ? parseInt(form.waymore_wayback_rl_retry) : 180,
        waymore_urlscan_rl_retry: Number.isFinite(parseInt(form.waymore_urlscan_rl_retry)) ? parseInt(form.waymore_urlscan_rl_retry) : 60,
        waymore_soft_timeout: Number.isFinite(parseInt(form.waymore_soft_timeout)) ? Math.max(60, Math.min(parseInt(form.waymore_soft_timeout), 86400)) : 1800,
        waymore_hard_timeout: Number.isFinite(parseInt(form.waymore_hard_timeout)) ? Math.max(60, Math.min(parseInt(form.waymore_hard_timeout), 86400)) : 3600,
        schedule: scheduleMode === 'cron'
          ? { type: 'cron', expr: cronExpr.trim() }
          : { type: 'interval', expr: intervalExpr },
        enabled: !!form.enabled,
        auto_enrich: !!form.auto_enrich,
        enable_classification: enableClassification,
        classification_prompt: classificationPrompt,
        classification_model: classificationModel,
        classification_parallel_hosts: Number.isFinite(parseInt(classificationParallelHosts)) ? parseInt(classificationParallelHosts) : 5,
      };
      if (classificationApiKey.trim()) {
        payload.classification_api_key = classificationApiKey.trim();
      }
      if (selectedDef && clearClassificationApiKey) {
        payload.clear_classification_api_key = true;
      }
      if (!selectedDef && !payload.classification_api_key) {
        payload.classification_api_key = '';
      }
      if (selectedDef) {
        await autodiscoveryAPI.updateDef(selectedDef._id, payload);
        showSuccess('Определение обновлено');
      } else {
        await autodiscoveryAPI.createDef(payload);
        showSuccess('Определение создано');
      }
      setSelectedDef(null);
      setForm({ ...EMPTY_FORM });
      setClassificationApiKey('');
      setClassificationApiKeyConfigured(false);
      setClearClassificationApiKey(false);
      setClassificationParallelHosts(5);
      loadDefs();
    } catch (e) {
      showError(e.response?.data?.message || 'Не удалось сохранить определение');
    }
  };

  const editDef = (d) => {
    setSelectedDef(d);
    setForm({
      name: d.name || '',
      domains: (d.domains || []).join(', '),
      additional_fqdns: (d.additional_fqdns || []).join(', '),
      whitelist_ips: (d.whitelist_ips || []).join(', '),
      whitelist_subdomains_regex: d.whitelist_subdomains_regex || '',
      exclude_hosts: (d.exclude_hosts || []).join(', '),
      blacklist_ips: (d.blacklist_ips || []).join(', '),
      provider_config: d.provider_config || '',
      enabled: !!d.enabled,
      auto_enrich: !!d.auto_enrich,
      katana_rate_limit: d.katana_rate_limit ?? 5,
      katana_workers: d.katana_workers ?? 1,
      katana_extra_flags: d.katana_extra_flags || '',
      httpx_ports: d.httpx_ports || '',
      enable_waymore: !!d.enable_waymore,
      waymore_threads: d.waymore_threads ?? 2,
      waymore_mode: d.waymore_mode || 'U',
      waymore_limit: d.waymore_limit ?? '',
      waymore_filter_codes: d.waymore_filter_codes ?? '301,302,304,404',
      waymore_filter_types: d.waymore_filter_types ?? 'text/css,image/jpeg,image/png,image/gif,image/svg+xml,font/woff,font/woff2',
      waymore_from_date: d.waymore_from_date || '',
      waymore_to_date: d.waymore_to_date || '',
      waymore_no_subs: !!d.waymore_no_subs,
      waymore_wayback_rl_retry: d.waymore_wayback_rl_retry ?? 180,
      waymore_urlscan_rl_retry: d.waymore_urlscan_rl_retry ?? 60,
      waymore_soft_timeout: d.waymore_soft_timeout ?? 1800,
      waymore_hard_timeout: d.waymore_hard_timeout ?? 3600,
    });
    const s = d.schedule || {};
    const isCron = s.type === 'cron';
    setScheduleMode(isCron ? 'cron' : 'interval');
    if (isCron) {
      setCronExpr(s.expr || '');
      setIntervalPreset('15m');
      setIntervalCustom('');
    } else {
      const expr = s.expr || '15m';
      setCronExpr('');
      // if matches presets, set preset, else put into custom
      if (['15m', '1h', '6h', '24h'].includes(expr)) {
        setIntervalPreset(expr);
        setIntervalCustom('');
      } else {
        setIntervalPreset('15m');
        setIntervalCustom(expr);
      }
    }
    // Classification fields
    setEnableClassification(d.enable_classification || false);
    setClassificationPrompt(d.classification_prompt || defaultPrompt);
    setClassificationModel(d.classification_model || 'openai/gpt-oss-20b:free');
    setClassificationApiKey('');
    setClassificationApiKeyConfigured(!!d.classification_api_key_configured);
    setClearClassificationApiKey(false);
    setClassificationParallelHosts(d.classification_parallel_hosts ?? 5);
  };

  const delDef = async (id) => {
    if (!window.confirm('Удалить это определение?')) return;
    try {
      await autodiscoveryAPI.deleteDef(id);
      showSuccess('Определение удалено');
      loadDefs();
    } catch (e) {
      showError(e.response?.data?.message || 'Не удалось удалить');
    }
  };

  const toggleEnable = async (d) => {
    try {
      if (d.enabled) {
        await autodiscoveryAPI.disableDef(d._id);
        showSuccess('Выключено');
      } else {
        await autodiscoveryAPI.enableDef(d._id);
        showSuccess('Включено');
      }
      loadDefs();
    } catch (e) {
      showError(e.response?.data?.message || 'Не удалось переключить состояние');
    }
  };

  const runNow = async (id) => {
    try {
      await autodiscoveryAPI.runNow(id);
      showSuccess('Запуск поставлен в очередь');
      loadRuns(id);
    } catch (e) {
      showError(e.response?.data?.message || 'Не удалось запланировать запуск');
    }
  };

  const cancelRun = async (id) => {
    try {
      await autodiscoveryAPI.cancelRun(id);
      showSuccess('Запуск отменён');
      loadRuns(selectedDef?._id || null);
    } catch (e) {
      showError(e.response?.data?.message || 'Не удалось отменить запуск');
    }
  };

  const enrichFromRun = async (run) => {
    if (!window.confirm('Это добавит результаты данного запуска к текущим хостам проекта (merge). Существующие хосты не будут удалены. Продолжить?')) {
      return;
    }
    try {
      // Load config_json for this run (stored in GridFS on backend)
      const cfgRes = await autodiscoveryAPI.getRunConfigJson(run._id);
      const configData = cfgRes.data?.config_json || [];
      if (!configData || configData.length === 0) {
        showError('В этом запуске нет данных config.json');
        return;
      }

      const res = await hostsAPI.mergeHosts(configData, run.def_id, run._id);
      const stats = res.data?.stats || {};
      showSuccess(`Хосты успешно обновлены: ${stats.created || 0} новых, ${stats.updated || 0} обновлено`);
    } catch (e) {
      showError(e.response?.data?.message || 'Не удалось обновить хосты');
    }
  };

  const viewRunResults = async (run) => {
    setViewingRunData(run);
    setShowViewModal(true);

    // Load config_json for this run (stored in GridFS on backend)
    try {
      const cfgRes = await autodiscoveryAPI.getRunConfigJson(run._id);
      setViewingRunData(prev => ({
        ...(prev || run),
        config_json: cfgRes.data?.config_json || [],
      }));
    } catch (e) {
      // Keep modal open; config tab will show empty
      setViewingRunData(prev => ({
        ...(prev || run),
        config_json: [],
      }));
    }
    
    // Load classification results if available
    try {
      const res = await autodiscoveryAPI.getClassificationResults(run._id);
      setClassificationResults(res.data.results || []);
    } catch (e) {
      console.error('Не удалось загрузить результаты классификации:', e);
      setClassificationResults([]);
    }
  };

  const getRunStatusLabel = (status) => {
    const map = {
      pending: 'в ожидании',
      running: 'выполняется',
      paused: 'на паузе (окно)',
      slot_wait: 'ожидает слот',
      pausing: 'ставится на паузу',
      cancelling: 'отменяется',
      completed: 'завершён',
      failed: 'ошибка',
      cancelled: 'отменён',
    };
    return map[status] || status;
  };

  const getScheduleTypeLabel = (type) => {
    if (type === 'interval') return 'интервал';
    if (type === 'cron') return 'CRON';
    return type;
  };

  return (
    <div style={{ padding: 28 }}>
      <div className="page-header">
        <div className="page-header-icon">
          <Globe size={24} />
        </div>
        <div className="page-header-text">
          <h1>АвтоДискавери</h1>
          <p>Автоматическое обнаружение и сканирование</p>
        </div>
      </div>

      {/* Definition form */}
      <form onSubmit={submitForm} className="card">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label className="form-label">Название</label>
            <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Лимит запросов Katana (-rl)</label>
            <input 
              className="form-input" 
              type="number"
              min="1"
              value={form.katana_rate_limit}
              onChange={e => setForm({ ...form, katana_rate_limit: e.target.value })}
              placeholder="5"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Воркеры Katana (параллельные процессы)</label>
            <input
              className="form-input"
              type="number"
              min="1"
              max="20"
              value={form.katana_workers}
              onChange={e => setForm({ ...form, katana_workers: e.target.value })}
              placeholder="1"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Доп. флаги Katana (через пробел, например: -jc -d 5 -depth 3)</label>
            <input
              className="form-input"
              type="text"
              value={form.katana_extra_flags}
              onChange={e => setForm({ ...form, katana_extra_flags: e.target.value })}
              placeholder="-jc -d 5 -kf all"
            />
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
              Разделяются пробелами. Поддерживаются как одиночные флаги, так и флаги со значениями.
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Порты HTTPx (через запятую, например 80,443,8080)</label>
            <textarea 
              className="form-input" 
              value={form.httpx_ports}
              onChange={e => setForm({ ...form, httpx_ports: e.target.value })}
              placeholder="80,443,8080,8443"
              rows={3}
              style={{ resize: 'vertical', minHeight: '60px' }}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Домены (через запятую или пробел)</label>
            <textarea
              className="form-input"
              value={form.domains}
              onChange={e => setForm({ ...form, domains: e.target.value })}
              placeholder="example.com, test.com"
              rows={3}
              style={{ resize: 'vertical', minHeight: '60px' }}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Дополнительные FQDN (через запятую/пробел, объединяются с результатами subfinder)</label>
            <textarea
              className="form-input"
              value={form.additional_fqdns}
              onChange={e => setForm({ ...form, additional_fqdns: e.target.value })}
              placeholder="example.com, subdomain.example.com"
              rows={3}
              style={{ resize: 'vertical', minHeight: '60px' }}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Белый список IP (через запятую)</label>
            <textarea 
              className="form-input" 
              value={form.whitelist_ips} 
              onChange={e => setForm({ ...form, whitelist_ips: e.target.value })}
              placeholder="например: 1.2.3.4, 5.6.7.8"
              rows={3}
              style={{ resize: 'vertical', minHeight: '60px' }}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Белый список поддоменов (regex-шаблон)</label>
            <textarea 
              className="form-input" 
              value={form.whitelist_subdomains_regex} 
              onChange={e => setForm({ ...form, whitelist_subdomains_regex: e.target.value })}
              placeholder="например: .*\.prod\..* или оставьте пустым, чтобы разрешить всё"
              rows={3}
              style={{ resize: 'vertical', minHeight: '60px' }}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Исключить хосты (через запятую)</label>
            <textarea 
              className="form-input" 
              value={form.exclude_hosts} 
              onChange={e => setForm({ ...form, exclude_hosts: e.target.value })}
              placeholder="например: test.example.com, dev.example.com"
              rows={3}
              style={{ resize: 'vertical', minHeight: '60px' }}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Чёрный список IP (через запятую, диапазоны/IP)</label>
            <textarea 
              className="form-input" 
              value={form.blacklist_ips} 
              onChange={e => setForm({ ...form, blacklist_ips: e.target.value })}
              placeholder="например: 1.2.3.4, 5.6.7.0/24"
              rows={3}
              style={{ resize: 'vertical', minHeight: '60px' }}
            />
          </div>
        </div>
        
        {/* Provider Config */}
        <div className="form-group" style={{ marginTop: 16 }}>
          <label className="form-label">Конфиг провайдеров (YAML)</label>
          <textarea 
            className="form-input" 
            value={form.provider_config} 
            onChange={e => setForm({ ...form, provider_config: e.target.value })}
            placeholder="bevigil: []&#10;bufferover: []&#10;..."
            rows={12}
            style={{ 
              fontFamily: 'monospace', 
              fontSize: '12px', 
              resize: 'vertical',
              width: '100%'
            }}
          />
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Оставьте пустым для значений по умолчанию. Формат: provider_name: []
          </div>
        </div>

        {/* Schedule controls */}
        <div className="form-group" style={{ marginTop: 8 }}>
          <label className="form-label">Расписание</label>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <input type="radio" name="scheduleMode" checked={scheduleMode === 'interval'} onChange={() => setScheduleMode('interval')} /> Интервал
              </label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <input type="radio" name="scheduleMode" checked={scheduleMode === 'cron'} onChange={() => setScheduleMode('cron')} /> CRON
              </label>
            </div>

            {scheduleMode === 'interval' ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <select className="form-input" style={{ width: 140 }} value={intervalPreset} onChange={e => setIntervalPreset(e.target.value)}>
                  <option value="15m">Каждые 15 минут</option>
                  <option value="1h">Каждый 1 час</option>
                  <option value="6h">Каждые 6 часов</option>
                  <option value="24h">Каждые 24 часа</option>
                </select>
                <input className="form-input" style={{ width: 160 }} placeholder="Свой интервал (например 45m или 2h)" value={intervalCustom} onChange={e => setIntervalCustom(e.target.value)} />
              </div>
            ) : (
              <input className="form-input" placeholder="*/15 * * * *" value={cronExpr} onChange={e => setCronExpr(e.target.value)} />
            )}
          </div>
        </div>

        {/* Waymore settings */}
        <div className="form-group" style={{ marginTop: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={!!form.enable_waymore}
              onChange={e => setForm({ ...form, enable_waymore: e.target.checked })}
            />
            Waymore (исторические URL из веб-архивов)
          </label>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, marginBottom: 8 }}>
            Запускается в фоне после старта pipeline по верхнеуровневым доменам.
            После katana найденные URL пробингуются httpx с rate-limit равным katana_rate_limit,
            результаты добавляются в locations с дедупом.
          </div>

          {form.enable_waymore && (
            <div style={{ marginTop: 12, padding: 16, backgroundColor: 'var(--bg-tertiary)', borderRadius: 6 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Параллельные потоки (1–5)</label>
                  <input
                    className="form-input"
                    type="number"
                    min="1"
                    max="5"
                    value={form.waymore_threads}
                    onChange={e => setForm({ ...form, waymore_threads: e.target.value })}
                  />
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                    Один waymore-процесс на домен. Cooldown 429 управляется отдельно ниже.
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Лимит URL на домен (пусто = без лимита)</label>
                  <input
                    className="form-input"
                    type="number"
                    min="1"
                    value={form.waymore_limit}
                    onChange={e => setForm({ ...form, waymore_limit: e.target.value })}
                    placeholder="например, 5000"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Фильтр HTTP-кодов (-fc, через запятую)</label>
                  <input
                    className="form-input"
                    type="text"
                    value={form.waymore_filter_codes}
                    onChange={e => setForm({ ...form, waymore_filter_codes: e.target.value })}
                    placeholder="301,302,304,404"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Фильтр MIME-типов (-ft, через запятую)</label>
                  <input
                    className="form-input"
                    type="text"
                    value={form.waymore_filter_types}
                    onChange={e => setForm({ ...form, waymore_filter_types: e.target.value })}
                    placeholder="text/css,image/jpeg,image/png,…"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Дата с (-from), например 2022 или 202201</label>
                  <input
                    className="form-input"
                    type="text"
                    value={form.waymore_from_date}
                    onChange={e => setForm({ ...form, waymore_from_date: e.target.value })}
                    placeholder="2022"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Дата по (-to)</label>
                  <input
                    className="form-input"
                    type="text"
                    value={form.waymore_to_date}
                    onChange={e => setForm({ ...form, waymore_to_date: e.target.value })}
                    placeholder="2025"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Cooldown 429 — Wayback (сек, -wrlr)</label>
                  <input
                    className="form-input"
                    type="number"
                    min="1"
                    max="3600"
                    value={form.waymore_wayback_rl_retry}
                    onChange={e => setForm({ ...form, waymore_wayback_rl_retry: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Cooldown 429 — URLScan (сек, -urlr)</label>
                  <input
                    className="form-input"
                    type="number"
                    min="1"
                    max="3600"
                    value={form.waymore_urlscan_rl_retry}
                    onChange={e => setForm({ ...form, waymore_urlscan_rl_retry: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Soft-таймаут на домен (сек)</label>
                  <input
                    className="form-input"
                    type="number"
                    min="60"
                    max="86400"
                    value={form.waymore_soft_timeout}
                    onChange={e => setForm({ ...form, waymore_soft_timeout: e.target.value })}
                  />
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                    Мягкая остановка (SIGTERM): частичные результаты сохраняются. Должен быть меньше hard.
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Hard-таймаут на домен (сек)</label>
                  <input
                    className="form-input"
                    type="number"
                    min="60"
                    max="86400"
                    value={form.waymore_hard_timeout}
                    onChange={e => setForm({ ...form, waymore_hard_timeout: e.target.value })}
                  />
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                    Жёсткий потолок: процесс убивается (SIGKILL).
                  </div>
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={!!form.waymore_no_subs}
                      onChange={e => setForm({ ...form, waymore_no_subs: e.target.checked })}
                    />
                    Не включать поддомены (-n)
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Classification settings */}
        <div className="form-group" style={{ marginTop: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
            <input
              type="checkbox"
              checked={enableClassification}
              onChange={e => setEnableClassification(e.target.checked)}
            />
            Включить классификацию
          </label>
          
          {enableClassification && (
            <div style={{ marginTop: 12, padding: 16, backgroundColor: 'var(--bg-tertiary)', borderRadius: 6 }}>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label">Модель</label>
                <input 
                  type="text"
                  className="form-input" 
                  value={classificationModel} 
                  onChange={e => setClassificationModel(e.target.value)}
                  style={{ width: '100%' }}
                  placeholder="openai/gpt-oss-20b:free"
                />
              </div>
              
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label">API-ключ</label>
                {classificationApiKeyConfigured && selectedDef && (
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>
                    API-ключ уже задан на бэкенде. Оставьте поле пустым, чтобы сохранить текущее значение.
                  </div>
                )}
                <input 
                  type="password"
                  className="form-input" 
                  value={classificationApiKey} 
                  onChange={e => setClassificationApiKey(e.target.value)}
                  style={{ width: '100%' }}
                  placeholder="Введите API-ключ для модели"
                />
                {selectedDef && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: '12px', color: 'var(--text-secondary)' }}>
                    <input
                      type="checkbox"
                      checked={clearClassificationApiKey}
                      onChange={e => setClearClassificationApiKey(e.target.checked)}
                    />
                    Удалить сохранённый API-ключ при сохранении
                  </label>
                )}
              </div>

              <div className="form-group" style={{ marginBottom: 12 }}>
                <label className="form-label">Параллельная классификация хостов (N)</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  className="form-input"
                  value={classificationParallelHosts}
                  onChange={e => setClassificationParallelHosts(e.target.value)}
                  style={{ width: '100%' }}
                  placeholder="5"
                />
              </div>
              
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <label className="form-label">Промпт классификации</label>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setClassificationPrompt(defaultPrompt)}
                    style={{ padding: '4px 12px', fontSize: '12px' }}
                  >
                    Сбросить к значению по умолчанию
                  </button>
                </div>
                <textarea 
                  className="form-input" 
                  value={classificationPrompt} 
                  onChange={e => setClassificationPrompt(e.target.value)}
                  rows={12}
                  style={{ 
                    fontFamily: 'monospace', 
                    fontSize: '12px', 
                    resize: 'vertical',
                    width: '100%'
                  }}
                  placeholder="Введите промпт классификации..."
                />
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} /> Включено
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={form.auto_enrich} onChange={e => setForm({ ...form, auto_enrich: e.target.checked })} /> Авто-обогащение
          </label>
          <button type="submit" className="btn btn-primary">{selectedDef ? 'Обновить' : 'Создать'}</button>
          {selectedDef && (
            <button type="button" className="btn btn-secondary" onClick={() => { 
              setSelectedDef(null); 
              setForm({ ...EMPTY_FORM });
              setScheduleMode('interval'); 
              setIntervalPreset('15m'); 
              setIntervalCustom(''); 
              setCronExpr(''); 
              setEnableClassification(false);
              setClassificationPrompt(defaultPrompt);
              setClassificationModel('openai/gpt-oss-20b:free');
              setClassificationApiKey('');
              setClassificationApiKeyConfigured(false);
              setClearClassificationApiKey(false);
              setClassificationParallelHosts(5);
            }}>Очистить</button>
          )}
        </div>
      </form>

      {/* Definitions table */}
      <div className="card" style={{ padding: 0 }}>
        <table className="table">
          <thead>
            <tr>
              <th>Название</th>
              <th>Домены</th>
              <th>Расписание</th>
              <th>Включено</th>
              <th>Последний запуск</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {defs.map(d => (
              <tr key={d._id}>
                <td>{d.name}</td>
                <td>{(d.domains || []).join(', ')}</td>
                <td>{d.schedule ? `${getScheduleTypeLabel(d.schedule.type)}: ${d.schedule.expr}` : '-'}</td>
                <td>
                  {d.enabled ? 'Да' : 'Нет'}
                  {d.auto_enrich && <span style={{ marginLeft: 6, color: 'var(--color-success)', fontSize: '11px' }}>+ merge</span>}
                </td>
                <td>{d.last_run?.at ? new Date(d.last_run.at).toLocaleString() : '-'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button className="btn btn-secondary" onClick={() => editDef(d)}>Изменить</button>
                    <button className="btn btn-secondary" onClick={() => toggleEnable(d)}>{d.enabled ? 'Выключить' : 'Включить'}</button>
                    <button className="btn btn-primary" onClick={() => runNow(d._id)}>Запустить</button>
                    <button className="btn btn-danger" onClick={() => delDef(d._id)}>Удалить</button>
                    <button className="btn" onClick={() => { setSelectedDef(d); loadRuns(d._id); }}>Запуски</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Progress panel for running scan */}
      {progressData?.progress && (progressData.status === 'running' || progressData.status === 'pending') && (
        <div className="card" style={{
          background: 'var(--color-info-bg)',
          border: '1px solid var(--color-info-border)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 10, height: 10, borderRadius: '50%',
                backgroundColor: 'var(--color-primary)',
                animation: 'pulse 1.5s ease-in-out infinite',
              }} />
              <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 15 }}>
                {progressData.progress.stage_label}
              </span>
            </div>
            <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
              {progressData.progress.percent}%
            </span>
          </div>

          {/* Progress bar */}
          <div style={{
            width: '100%',
            height: 6,
            backgroundColor: 'var(--bg-tertiary)',
            borderRadius: 3,
            overflow: 'hidden',
            marginBottom: 12,
          }}>
            <div style={{
              width: `${progressData.progress.percent}%`,
              height: '100%',
              backgroundColor: 'var(--color-primary)',
              borderRadius: 3,
              transition: 'width 0.5s ease',
            }} />
          </div>

          <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 8 }}>
            {progressData.progress.detail}
          </div>

          {/* Stats grid */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {progressData.progress.discovered_hosts != null && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{progressData.progress.discovered_hosts}</span> поддоменов
              </div>
            )}
            {progressData.progress.processed_hosts != null && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{progressData.progress.processed_hosts}</span>
                {progressData.progress.total_hosts ? ` / ${progressData.progress.total_hosts}` : ''} обработано
              </div>
            )}
            {progressData.progress.locations_found != null && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                <span style={{ fontWeight: 600, color: 'var(--color-success)' }}>{progressData.progress.locations_found}</span> локаций
              </div>
            )}
            {progressData.progress.relations_found != null && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                <span style={{ fontWeight: 600, color: 'var(--color-warning)' }}>{progressData.progress.relations_found}</span> связей
              </div>
            )}
            {progressData.progress.current_host && (
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                {progressData.progress.current_host}
              </div>
            )}
            {progressData.progress.classified != null && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                <span style={{ fontWeight: 600, color: 'var(--color-info)' }}>{progressData.progress.classified}</span>
                {progressData.progress.total_to_classify ? ` / ${progressData.progress.total_to_classify}` : ''} классифицировано
              </div>
            )}
          </div>
          <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
        </div>
      )}

      {/* Runs list */}
      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: 12, background: 'var(--bg-tertiary)' }}>Запуски {selectedDef ? `для ${selectedDef.name}` : ''}</div>
        <table className="table">
          <thead>
            <tr>
              <th>Создан</th>
              <th>Статус</th>
              <th>Начат</th>
              <th>Завершён</th>
              <th>Хосты</th>
              <th>Локации</th>
              <th>Связи</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {runs.map(r => (
              <tr key={r._id}>
                <td>{r.created_at ? new Date(r.created_at).toLocaleString() : '-'}</td>
                <td>
                  <span className={`badge ${
                    r.status === 'completed' ? 'badge-success' :
                    r.status === 'running' ? 'badge-info' :
                    r.status === 'failed' ? 'badge-danger' :
                    r.status === 'cancelled' ? 'badge-warning' :
                    'badge-neutral'
                  }`}>
                    {getRunStatusLabel(r.status)}
                  </span>
                </td>
                <td style={{ fontSize: '12px' }}>{r.started_at ? new Date(r.started_at).toLocaleString() : '-'}</td>
                <td style={{ fontSize: '12px' }}>{r.completed_at ? new Date(r.completed_at).toLocaleString() : '-'}</td>
                <td>
                  <span className="badge badge-info">
                    {r.stats?.processed_hosts ?? r.stats?.discovered_hosts ?? '-'}
                  </span>
                </td>
                <td>
                  <span className="badge badge-success">
                    {r.stats?.total_locations ?? '-'}
                  </span>
                </td>
                <td>
                  <span className="badge badge-warning">
                    {r.stats?.total_relations ?? '-'}
                  </span>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {['running', 'pending', 'paused', 'slot_wait'].includes(r.status) && (
                      <button className="btn btn-secondary" onClick={() => cancelRun(r._id)}>Отменить</button>
                    )}
                    {r.status === 'completed' && (
                      <>
                        <button className="btn btn-secondary" onClick={() => viewRunResults(r)}>Предпросмотр</button>
                        {defs.find(d => d._id === r.def_id)?.auto_enrich ? (
                          <span style={{ color: 'var(--color-success)', fontSize: '12px', padding: '4px 8px' }}>Авто-обогащено</span>
                        ) : (
                          <button className="btn btn-primary" onClick={() => enrichFromRun(r)}>Merge в хосты</button>
                        )}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* View Results Modal */}
      {showViewModal && viewingRunData && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            width: '900px',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '16px',
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: 'var(--text-primary)',
              }}>
                Результаты АвтоДискавери
              </h2>
              <button
                onClick={() => setShowViewModal(false)}
                style={{
                  padding: '4px 12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                }}
              >
                Закрыть
              </button>
            </div>

            <div style={{
              backgroundColor: 'var(--bg-tertiary)',
              padding: '12px',
              borderRadius: '6px',
              marginBottom: '16px',
              fontSize: '14px',
              color: 'var(--text-secondary)',
            }}>
              <div><strong>Статус:</strong> {getRunStatusLabel(viewingRunData.status)}</div>
              <div><strong>Хосты:</strong> {viewingRunData.stats?.processed_hosts ?? viewingRunData.stats?.discovered_hosts ?? 0}</div>
              <div><strong>Всего локаций:</strong> {viewingRunData.stats?.total_locations ?? 0}</div>
              <div><strong>Всего связей:</strong> {viewingRunData.stats?.total_relations ?? 0}</div>
              {viewingRunData.classification_stats && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border-color)' }}>
                  <div><strong>Классификация:</strong> {viewingRunData.classification_stats.classified} / {viewingRunData.classification_stats.total} хостов классифицировано</div>
                  {viewingRunData.classification_stats.failed > 0 && (
                    <div style={{ color: 'var(--color-warning)' }}>Ошибок: {viewingRunData.classification_stats.failed}</div>
                  )}
                </div>
              )}
            </div>

            {/* Tabs for Config and Classification */}
            <div style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', gap: '8px', borderBottom: '2px solid var(--border-color)' }}>
                <button
                  onClick={() => setViewingRunData({ ...viewingRunData, activeTab: 'config' })}
                  style={{
                    padding: '8px 16px',
                    border: 'none',
                    backgroundColor: 'transparent',
                    color: viewingRunData.activeTab === 'config' || !viewingRunData.activeTab ? 'var(--text-primary)' : 'var(--text-secondary)',
                    borderBottom: viewingRunData.activeTab === 'config' || !viewingRunData.activeTab ? '2px solid var(--color-primary)' : 'none',
                    cursor: 'pointer',
                    fontWeight: viewingRunData.activeTab === 'config' || !viewingRunData.activeTab ? 'bold' : 'normal',
                  }}
                >
                  config.json
                </button>
                {classificationResults.length > 0 && (
                  <button
                    onClick={() => setViewingRunData({ ...viewingRunData, activeTab: 'classification' })}
                    style={{
                      padding: '8px 16px',
                      border: 'none',
                      backgroundColor: 'transparent',
                      color: viewingRunData.activeTab === 'classification' ? 'var(--text-primary)' : 'var(--text-secondary)',
                      borderBottom: viewingRunData.activeTab === 'classification' ? '2px solid var(--color-primary)' : 'none',
                      cursor: 'pointer',
                      fontWeight: viewingRunData.activeTab === 'classification' ? 'bold' : 'normal',
                    }}
                  >
                    Результаты классификации ({classificationResults.length})
                  </button>
                )}
              </div>
            </div>

            <div className="code-preview" style={{
              flex: 1,
              overflow: 'auto',
            }}>
              {(!viewingRunData.activeTab || viewingRunData.activeTab === 'config') && (() => {
                const jsonString = JSON.stringify(viewingRunData.config_json || [], null, 2);
                const jsonBytes = new TextEncoder().encode(jsonString).length;
                const maxBytes = 5000;
                const isTruncated = jsonBytes > maxBytes;
                const truncatedJson = isTruncated 
                  ? new TextDecoder().decode(new TextEncoder().encode(jsonString).slice(0, maxBytes))
                  : jsonString;
                
                return (
                  <>
                    {isTruncated && (
                      <div style={{
                        marginBottom: '12px',
                        padding: '8px',
                        backgroundColor: 'var(--color-warning-bg)',
                        color: 'var(--color-warning-text)',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        ⚠️ Предпросмотр JSON ограничен первыми {maxBytes} байтами (всего: {jsonBytes.toLocaleString()} байт)
                      </div>
                    )}
                    <pre style={{ margin: 0 }}>
                      {truncatedJson}
                    </pre>
                  </>
                );
              })()}
              
              {viewingRunData.activeTab === 'classification' && classificationResults.length > 0 && (
                <div style={{ fontFamily: 'inherit' }}>
                  {classificationResults.map((result, idx) => (
                    <div key={idx} style={{ marginBottom: '24px', padding: '12px', backgroundColor: 'var(--bg-tertiary)', borderRadius: '6px', border: '1px solid var(--border-color)' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '12px', color: 'var(--color-warning)' }}>
                        {result.host}
                      </div>
                      {result.status === 'failed' ? (
                        <div style={{ color: 'var(--color-danger)' }}>Ошибка классификации: {result.error}</div>
                      ) : (
                        <div>
                          {Object.entries(result.classification || {}).map(([category, items]) => (
                            items && items.length > 0 && (
                              <div key={category} style={{ marginBottom: '12px' }}>
                                <div style={{ fontWeight: 'bold', color: 'var(--color-primary)', marginBottom: '4px', textTransform: 'capitalize' }}>
                                  {category.replace(/_/g, ' ')}:
                                </div>
                                <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
                                  {items.map((item, i) => (
                                    <li key={i} style={{ marginBottom: '2px' }}>{item}</li>
                                  ))}
                                </ul>
                              </div>
                            )
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              marginTop: '16px',
              justifyContent: 'flex-end',
            }}>
              <button
                onClick={() => {
                  const blob = new Blob([JSON.stringify(viewingRunData.config_json || [], null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `autodiscovery-${viewingRunData._id}.json`;
                  link.click();
                  URL.revokeObjectURL(url);
                }}
                className="btn btn-secondary"
              >
                Скачать JSON
              </button>
              <button
                onClick={() => setShowViewModal(false)}
                className="btn btn-primary"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoDiscovery;
